import React, { useState, useCallback, useMemo } from 'react';
import { FiNavigation, FiX, FiExternalLink } from 'react-icons/fi';

export function buildGoogleMapsLocationUrl(lat, lng) {
  return `https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`;
}

/**
 * Location picker: uses the browser Geolocation API to capture
 * { lat, lng, mapUrl } (Google Maps link to the point).
 * Works on HTTPS (or localhost). No API key required.
 */
function MapLocationField({ value, onChange, disabled = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasCoords = value
    && typeof value === 'object'
    && typeof value.lat === 'number'
    && typeof value.lng === 'number'
    && !Number.isNaN(value.lat)
    && !Number.isNaN(value.lng);

  const openMapsUrl = useMemo(() => {
    if (!hasCoords) return '';
    if (value.mapUrl && typeof value.mapUrl === 'string' && value.mapUrl.trim().startsWith('http')) {
      return value.mapUrl.trim();
    }
    return buildGoogleMapsLocationUrl(value.lat, value.lng);
  }, [hasCoords, value]);

  const requestPosition = useCallback(() => {
    if (disabled) return;
    setError(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Location is not available in this browser.');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        onChange({
          lat,
          lng,
          mapUrl: buildGoogleMapsLocationUrl(lat, lng),
        });
      },
      (err) => {
        setLoading(false);
        // GeolocationPositionError: 1 = permission, 2 = unavailable, 3 = timeout
        if (err.code === 1) {
          setError('Location access was denied. Allow location in your browser settings and try again.');
        } else if (err.code === 2) {
          setError('Your position could not be determined. Try again or check your device settings.');
        } else if (err.code === 3) {
          setError('Getting your location took too long. Please try again.');
        } else {
          setError('Could not get your location. Please try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, [disabled, onChange]);

  if (disabled) {
    return (
      <div
        style={{
          padding: 14,
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          fontSize: 13,
          color: '#6b7280',
          lineHeight: 1.5,
        }}
      >
        Respondents will use <strong>Use my current location</strong> to capture their device’s GPS
        point. A <strong>Google Maps link</strong> plus coordinates are saved with the response
        (exports include the link for one-click open).
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={requestPosition}
          disabled={loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            background: loading ? '#9ca3af' : '#1f2937',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s ease',
          }}
        >
          <FiNavigation size={18} style={{ flexShrink: 0 }} />
          {loading ? 'Getting location…' : 'Use my current location'}
        </button>
        {hasCoords && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              onChange(null);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              background: 'white',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
            }}
            title="Clear location"
          >
            <FiX size={16} />
            Clear
          </button>
        )}
      </div>
      {error && (
        <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 10, marginBottom: 0, lineHeight: 1.45 }}>
          {error}
        </p>
      )}
      {typeof window !== 'undefined' && window.isSecureContext === false && window.location?.hostname !== 'localhost' && (
        <p style={{ color: '#b45309', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          This page must be served over HTTPS to use the location picker, except on localhost.
        </p>
      )}
      {hasCoords && openMapsUrl && (
        <div style={{ marginTop: 12 }}>
          <a
            href={openMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              fontWeight: 500,
              color: '#2563eb',
              textDecoration: 'none',
            }}
          >
            <FiExternalLink size={16} />
            Open in Google Maps
          </a>
          <p
            style={{
              fontSize: 12,
              color: '#6b7280',
              marginTop: 6,
              marginBottom: 0,
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
          </p>
        </div>
      )}
      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 0 }}>
        Your device may ask to share your location. Coordinates and a maps link are stored with
        this response.
      </p>
    </div>
  );
}

export default MapLocationField;
